<script setup lang="ts">
import AppButton from '@/components/ui/AppButton.vue'
import Toggle from '@/components/ui/Toggle.vue'
import Modal from '@/components/ui/Modal.vue'
import PhoneInput from '@/components/ui/PhoneInput.vue'
import type { Country, ErrorEvent, WhatsappEvent } from '@/types/whatsapp-event'
import { computed, onMounted, reactive, ref } from 'vue'
import { confirmDiagnosePhone } from '@/services/whatsapp'
import WhatsappContent from '@/lang/whatsapp.json'
import { useLang } from '@/composables/use-lang'

const props = defineProps({
  aws_url: {
    type: String,
    required: true
  }
})

const langContent = useLang(WhatsappContent)

const modal = ref<InstanceType<typeof Modal> | null>(null)

const isVisible = ref<boolean>(false)
const loading = ref<boolean>(false)
const showMore = ref<boolean>(false)
const error = reactive<ErrorEvent>({
  status: false,
  message: ''
})
const country = ref<Country>({
  name: '',
  dialCode: ''
})

const formData = reactive<WhatsappEvent>({
  phone_number: '',
  additional_phone_number: '',
  lang: 'nl'
})

const disabledButton = computed(() => {
  return !!(loading.value || error.status || !formData.phone_number)
})

const emit = defineEmits(['scroll'])

const setCountry = (data: Country) => {
  country.value = data
}

const mapUrlPath = () => {
  const clientId = country.value.dialCode + formData.phone_number
  return `?accepted=1&clientId=${clientId}&country=${country.value.name}&countryCode=${country.value.dialCode}`
}

const onSubmit = async () => {
  loading.value = true
  try {
    const payload = {
      ...formData,
      phone_number: `${country.value.dialCode}${formData.phone_number}`,
      url_path: mapUrlPath()
    }
    const result = await confirmDiagnosePhone(payload, props.aws_url)
    if (result.error) {
      setError(result.error)
      return null
    }
  } catch (e) {
    console.log('Error => ', error)
    setError(`${e}`)
  } finally {
    loading.value = false
    modal.value?.open()
  }
}

const setError = (msg: string) => {
  error.status = true
  error.message = msg
}

const resetError = () => {
  error.status = false
  error.message = ''
}

const handleShowMore = () => {
  showMore.value = !showMore.value
}

onMounted(() => {
  emit('scroll')
})
</script>

<template>
  <form
    v-if="langContent"
    @submit.prevent="onSubmit"
    class="flex flex-col items-center text-center"
  >
    <p class="w-full md:w-1/2 mb-5">{{ langContent.contact_question }}</p>

    <phone-input
      :placeholder="langContent.phone_placeholder"
      v-model="formData.phone_number"
      @setError="setError"
      @resetError="resetError"
      @setCountry="setCountry"
      :country="country"
    />

    <p class="w-full my-5 md:w-1/2">{{ langContent.additional_phone_question }}</p>

    <toggle class="mb-5" :isVisible="isVisible" v-model="isVisible" />

    <phone-input
      v-if="isVisible"
      :placeholder="langContent.additional_phone_placeholder"
      v-model="formData.additional_phone_number"
      @setError="setError"
      @resetError="resetError"
    />

    <p v-if="error.status" class="text-sweet-brown">{{ error.message }}</p>

    <div class="flex justify-center mt-5">
      <app-button type="submit" :loading="loading" :disabled="disabledButton">{{
        langContent.submit_button
      }}</app-button>
    </div>

    <modal ref="modal">
      <p>{{ langContent.modal.heading }}</p>
      <p>
        {{ langContent.modal.body1 }}
        <a class="text-english-vermillion" href="https://wa.me">WhatsApp</a>
        {{ langContent.modal.body2 }}
      </p>
      <p class="mb-10">
        <strong
          >{{ langContent.modal.no_verification }},
          <a href="#" class="text-english-vermillion" @click="handleShowMore">{{
            langContent.modal.link
          }}</a></strong
        >
      </p>

      <p class="mt-10" v-if="showMore">
        {{ langContent.modal.redirect_message.header }}<br />
        {{ langContent.modal.redirect_message.send_whatsapp }}
        <a class="text-english-vermillion" href="https://wa.me/31638746918">+31 6 38746918</a>
      </p>
    </modal>
  </form>
</template>

<style scoped></style>
